import React from 'react';
import {
    DropdownSelect,
    type SingleValue,
    components,
    type OptionProps,
    type SingleValueProps,
    type GroupBase,
    type MenuProps,
    type MenuListProps,
} from 'src/components/DropdownWrapper/DropdownWrapper';
import classNames from 'classnames';

import { Popup } from 'src/components/Popup/Popup';
import {
    ActionDropdownWrapper,
    ActionDropdownContainer,
    EditLink,
    OptionContainer,
    OptionLabel,
    AddButton,
    CustomOptionWrapper,
    ActionMenuOuterWrapper,
    ActionMenuListWrapper,
    getActionDropdownStyles,
} from './ActionDropdown.style';

interface OptionType {
    id: string | number;
    label?: string;
    name?: string;
    value?: unknown;
    toJS?: () => unknown;
}

interface ActionDropdownPropsType {
    label?: string;
    className?: string;
    onEdit?: (option: OptionType, data?: unknown) => void;
    onAdd?: (data?: unknown) => void;
    onChange?: (value: unknown) => void;
    onFocus?: () => void;
    onBlur?: () => void;
    onInputChange?: (value: string) => void;
    getAddForm?: (onSave: (data: unknown) => void, data: unknown) => React.ReactNode;
    getEditForm?: (onSave: (data: unknown) => void, data: unknown) => React.ReactNode;
    value?: unknown;
    addButton?: boolean;
    searchable?: boolean;
    clearable?: boolean;
    placeholder?: string;
    stopCustomFilter?: boolean;
    options?: OptionType[];
    name?: string;
    ignoreCase?: boolean;
    noResultsText?: string;
    searchPromptText?: string;
    trimFilter?: boolean;
    isShorten: boolean;
}

interface ActionDropdownStateType {
    value?: unknown;
    focus?: boolean;
    action?: 'add' | 'edit' | null;
    option?: OptionType;
    menuIsOpen?: boolean;
}

function preventDefault(e: React.MouseEvent<HTMLAnchorElement>): void {
    e.stopPropagation();
    e.preventDefault();
}

export class ActionDropdown extends React.PureComponent<ActionDropdownPropsType, ActionDropdownStateType> {
    public static defaultProps: Partial<ActionDropdownPropsType> = {
        searchable: false,
        clearable: false,
        placeholder: '',
    };

    public constructor(props: ActionDropdownPropsType) {
        super(props);

        this.state = {
            value: props.value,
        };
    }

    public componentDidUpdate(prevProps: ActionDropdownPropsType): void {
        if (prevProps.value !== this.props.value && this.props.value !== this.state.value) {
            this.setState({
                value: this.props.value,
            });
        }
    }

    private onChange = (option: SingleValue<OptionType>): void => {
        const value = option?.id ?? null;

        this.setState({
            value,
        });

        if (this.props.onChange !== undefined) {
            this.props.onChange(value);
        }
    };

    private onFocus = (): void => {
        this.setState({
            focus: true,
        });

        if (this.props.onFocus !== undefined) {
            this.props.onFocus();
        }
    };

    private onBlur = (): void => {
        this.setState({
            focus: false,
        });

        if (this.props.onBlur !== undefined) {
            this.props.onBlur();
        }
    };

    private onEdit = (option: OptionType) => {
        return (e: React.MouseEvent<HTMLAnchorElement>): void => {
            const { onEdit, getEditForm } = this.props;

            e.preventDefault();
            e.stopPropagation();

            if (getEditForm !== undefined) {
                this.setState({
                    action: 'edit',
                    option,
                });

                return;
            }

            if (onEdit !== undefined) {
                onEdit(option);
            }
        };
    };

    private onAdd = (e: React.MouseEvent<HTMLAnchorElement>): void => {
        const { onAdd, getAddForm } = this.props;

        e.preventDefault();

        if (getAddForm !== undefined) {
            this.setState({
                action: 'add',
            });

            return;
        }

        if (onAdd !== undefined) {
            onAdd();
        }
    };

    private add = (data: unknown): void => {
        if (this.props.onAdd !== undefined) {
            this.props.onAdd(data);
        }
        this.closeModals();
    };

    private save = (data: unknown): void => {
        if (this.props.onEdit !== undefined && this.state.option !== undefined) {
            this.props.onEdit(this.state.option, data);
        }
    };

    private closeModals = (): void => {
        this.setState({
            action: null,
        });
    };

    private CustomSingleValue = (props: SingleValueProps<OptionType, false, GroupBase<OptionType>>): JSX.Element => {
        const { data } = props;
        return (
            <components.SingleValue {...props}>
                <span>{data.label ?? data.name ?? ''}</span>
            </components.SingleValue>
        );
    };

    private CustomMenu = (props: MenuProps<OptionType, false, GroupBase<OptionType>>): JSX.Element => (
        <ActionMenuOuterWrapper>
            <components.Menu {...props}>{props.children}</components.Menu>
        </ActionMenuOuterWrapper>
    );

    private CustomMenuList = (props: MenuListProps<OptionType, false, GroupBase<OptionType>>): JSX.Element => (
        <ActionMenuListWrapper>
            <components.MenuList {...props}>{props.children}</components.MenuList>
        </ActionMenuListWrapper>
    );

    private CustomOption = (props: OptionProps<OptionType, false, GroupBase<OptionType>>): JSX.Element => {
        const { data, innerRef, innerProps, isSelected, isFocused } = props;
        let editTrigger: JSX.Element | undefined;

        if (Boolean(data.value)) {
            editTrigger = (
                <EditLink
                    href='#'
                    onMouseDown={this.onEdit(data)}
                    onClick={preventDefault}
                    data-test='action-dropdown-edit'
                >
                    E
                </EditLink>
            );
        }

        const label = data.label ?? data.name ?? '';

        return (
            <CustomOptionWrapper
                ref={innerRef}
                {...innerProps}
                data-test={`sport-select-${data.id}`}
                isSelected={isSelected}
                isFocused={isFocused}
            >
                <OptionContainer>
                    <OptionLabel isShorten={this.props.isShorten}>{label}</OptionLabel>
                    {editTrigger}
                </OptionContainer>
            </CustomOptionWrapper>
        );
    };

    private onMenuOpen = (): void => {
        this.setState({ menuIsOpen: true });
    };

    private onMenuClose = (): void => {
        this.setState({ menuIsOpen: false });
    };

    public render(): JSX.Element {
        const { label, getAddForm, getEditForm } = this.props;
        const { action, option, menuIsOpen } = this.state;

        const className = classNames([this.props.className, { focus: this.state.focus }]);

        const isStopCustomFilterOn = this.props.stopCustomFilter === true;

        let addPopup: JSX.Element | undefined;
        if (getAddForm !== undefined) {
            let addForm: React.ReactNode;
            if (action === 'add') {
                addForm = getAddForm(this.add, {});
            }

            addPopup = (
                <Popup
                    priority={1000}
                    onClose={this.closeModals}
                    active={action === 'add'}
                    title={`Add ${label ?? ''}`}
                >
                    {addForm}
                </Popup>
            );
        }

        let editPopup: JSX.Element | undefined;
        if (getEditForm !== undefined) {
            let editForm: React.ReactNode;
            if (action === 'edit') {
                const optionData = option?.toJS === undefined ? option ?? {} : option.toJS();
                editForm = getEditForm(this.save, optionData);
            }

            editPopup = (
                <Popup
                    priority={1000}
                    onClose={this.closeModals}
                    active={action === 'edit'}
                    title={`Edit ${label ?? ''}`}
                >
                    {editForm}
                </Popup>
            );
        }

        const foundOption = this.props.options?.find((opt) => opt.id === this.state.value);
        const selectedOption = foundOption === undefined ? null : foundOption;

        return (
            <ActionDropdownWrapper className={className}>
                {addPopup}
                {editPopup}
                <ActionDropdownContainer>
                    <DropdownSelect<OptionType, false>
                        value={selectedOption}
                        options={this.props.options}
                        onChange={this.onChange}
                        onBlur={this.onBlur}
                        onFocus={this.onFocus}
                        onInputChange={this.props.onInputChange}
                        onMenuOpen={this.onMenuOpen}
                        onMenuClose={this.onMenuClose}
                        isSearchable={this.props.searchable}
                        isClearable={this.props.clearable}
                        placeholder={this.props.placeholder}
                        noOptionsMessage={() => this.props.noResultsText ?? 'No results found'}
                        components={{
                            Option: this.CustomOption,
                            SingleValue: this.CustomSingleValue,
                            Menu: this.CustomMenu,
                            MenuList: this.CustomMenuList,
                        }}
                        customStyles={getActionDropdownStyles<OptionType, false>()}
                        getOptionValue={(option): string => option.id.toString()}
                        getOptionLabel={(option): string => option.label ?? option.name ?? ''}
                        filterOption={isStopCustomFilterOn ? (): boolean => true : undefined}
                        hasInternalLabel={true}
                    />
                    {this.props.addButton !== false && menuIsOpen === true && (
                        <AddButton
                            href='#'
                            onMouseDown={this.onAdd}
                            onClick={preventDefault}
                        >
                            + Add
                        </AddButton>
                    )}
                </ActionDropdownContainer>
            </ActionDropdownWrapper>
        );
    }
}
